import type { ReactNode } from 'react';
import { DEFAULT_AVATAR_BODY, type Slot } from '../models/types';
import { colors } from '../theme';

/** Where a slot's item sits on a body: `x`/`y` centre and `size` as fractions of the square canvas. */
export interface SlotAnchor {
  x: number;
  y: number;
  size: number;
}

export interface AvatarBodyDefinition {
  /** The figure seen from the front and from behind, drawn in a 0..1 coordinate space. */
  draw: (side: 'front' | 'back') => ReactNode;
  /** Single-item slots. `feet` is not here: shoes come as a pair, see `footAnchors`. */
  anchors: Partial<Record<Slot, SlotAnchor>>;
  /** Where the shoe on each leg sits, screen-left foot then screen-right foot. */
  footAnchors: { left: SlotAnchor; right: SlotAnchor };
  /** Where `body`-slot leg wear (trousers) sits: on the legs rather than the torso. */
  legsAnchor: SlotAnchor;
  /** `body`-slot items worn on the legs. Interim, until the catalog says so itself. */
  legWear: ReadonlySet<string>;
}

/** Slots that cannot be seen from behind. */
export const FRONT_ONLY: ReadonlySet<Slot> = new Set<Slot>(['body', 'face']);
/** Slots whose item sits on the other side of the figure when seen from behind. */
export const MIRRORED_ON_BACK: ReadonlySet<Slot> = new Set<Slot>(['hand', 'companion']);

const SKIN = '#FFD6B8';
const SHIRT = colors.sky;
const LEGS = '#7B83EB';
const OUTLINE = 'rgba(43, 45, 66, 0.25)';

function limb(x1: number, y1: number, x2: number, y2: number, color: string, width: number) {
  return <line x1={x1} y1={y1} x2={x2} y2={y2} stroke={color} strokeWidth={width} strokeLinecap="round" />;
}

/** A soft, rounded figure in the bubbly palette: round head, rounded torso, arms and legs. */
function drawBasic(side: 'front' | 'back'): ReactNode {
  // The smile: lower half of an ellipse, as in the Flutter painter (start 0.2 rad, sweep 2.74 rad).
  const cx = 0.5, cy = 0.27, rx = 0.04, ry = 0.025;
  const a0 = 0.2, a1 = 0.2 + 2.74;
  const smile = `M ${cx + rx * Math.cos(a0)} ${cy + ry * Math.sin(a0)} A ${rx} ${ry} 0 0 1 ${cx + rx * Math.cos(a1)} ${cy + ry * Math.sin(a1)}`;
  return (
    <>
      {/* Legs and arms first so the torso overlaps their joints. */}
      {limb(0.44, 0.66, 0.42, 0.88, LEGS, 0.085)}
      {limb(0.56, 0.66, 0.58, 0.88, LEGS, 0.085)}
      {limb(0.36, 0.44, 0.24, 0.6, SKIN, 0.07)}
      {limb(0.64, 0.44, 0.76, 0.6, SKIN, 0.07)}
      <rect x={0.34} y={0.38} width={0.32} height={0.32} rx={0.09} fill={SHIRT} stroke={OUTLINE} strokeWidth={0.012} />
      <circle cx={0.5} cy={0.25} r={0.13} fill={SKIN} stroke={OUTLINE} strokeWidth={0.012} />
      {side === 'front' && (
        <>
          <circle cx={0.455} cy={0.24} r={0.012} fill={colors.ink} />
          <circle cx={0.545} cy={0.24} r={0.012} fill={colors.ink} />
          <path d={smile} fill="none" stroke={colors.ink} strokeWidth={0.01} strokeLinecap="round" />
        </>
      )}
    </>
  );
}

export const avatarBodies: Record<string, AvatarBodyDefinition> = {
  [DEFAULT_AVATAR_BODY]: {
    draw: drawBasic,
    // The legs end at x 0.42 and 0.58, y 0.88; each shoe is centred under its leg.
    footAnchors: { left: { x: 0.42, y: 0.915, size: 0.16 }, right: { x: 0.58, y: 0.915, size: 0.16 } },
    // Trousers cover both legs: centred between hip (y 0.66) and ankle (y 0.88), spanning x 0.42..0.58.
    legsAnchor: { x: 0.5, y: 0.77, size: 0.26 },
    legWear: new Set(['giggling-pebble']),
    anchors: {
      back: { x: 0.5, y: 0.5, size: 0.44 },
      body: { x: 0.5, y: 0.53, size: 0.26 },
      neck: { x: 0.5, y: 0.385, size: 0.13 },
      face: { x: 0.5, y: 0.25, size: 0.15 },
      head: { x: 0.5, y: 0.1, size: 0.22 },
      hand: { x: 0.21, y: 0.61, size: 0.2 },
      companion: { x: 0.84, y: 0.3, size: 0.18 },
    },
  },
};

/** The definition for `bodyId`, falling back to `basic` for bodies this build lacks. */
export const avatarBodyFor = (bodyId: string): AvatarBodyDefinition => avatarBodies[bodyId] ?? avatarBodies[DEFAULT_AVATAR_BODY];
